import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Users, 
  Settings, 
  Shield, 
  Database, 
  Building2, 
  FileText,
  Bell,
  Eye,
  Download,
  Upload,
  HardDrive,
  BarChart3,
  UserPlus,
  Key,
  Activity
} from 'lucide-react';

const AdminQuickActions: React.FC = () => {
  const navigate = useNavigate();

  const userManagementActions = [
    {
      title: 'Manage Users',
      description: 'View and manage user accounts',
      icon: <Users className="h-5 w-5" />,
      onClick: () => navigate('/admin/users'),
      color: 'bg-accent-color-500 hover:bg-accent-color'
    },
    {
      title: 'User Roles',
      description: 'Configure user roles and permissions',
      icon: <Key className="h-5 w-5" />,
      onClick: () => navigate('/admin/users'),
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      title: 'View User Activity',
      description: 'Monitor user login and activity',
      icon: <Activity className="h-5 w-5" />,
      onClick: () => navigate('/admin/audit-logs'),
      color: 'bg-purple-500 hover:bg-purple-600'
    }
  ];

  const systemManagementActions = [
    {
      title: 'Notifications',
      description: 'Manage system notifications',
      icon: <Bell className="h-5 w-5" />,
      onClick: () => navigate('/admin/notifications'),
      color: 'bg-orange-500 hover:bg-orange-600'
    },
    {
      title: 'Audit Logs',
      description: 'Review system audit trail',
      icon: <Eye className="h-5 w-5" />,
      onClick: () => navigate('/admin/audit-logs'),
      color: 'bg-indigo-500 hover:bg-indigo-600'
    },
    {
      title: 'System Settings',
      description: 'Configure system parameters',
      icon: <Settings className="h-5 w-5" />,
      onClick: () => navigate('/admin/settings'),
      color: 'bg-gray-500 hover:bg-gray-600'
    }
  ];

  const dataManagementActions = [
    {
      title: 'Export Reports',
      description: 'Generate and download reports',
      icon: <Download className="h-5 w-5" />,
      onClick: () => navigate('/admin/reports'),
      color: 'bg-teal-500 hover:bg-teal-600'
    },
    {
      title: 'Backup Data',
      description: 'Create system backup',
      icon: <HardDrive className="h-5 w-5" />,
      onClick: () => navigate('/admin/backup'),
      color: 'bg-red-500 hover:bg-red-600'
    },
    {
      title: 'Import Data',
      description: 'Import data from external sources',
      icon: <Upload className="h-5 w-5" />,
      onClick: () => navigate('/admin/import'),
      color: 'bg-cyan-500 hover:bg-cyan-600'
    }
  ];

  const companyManagementActions = [
    {
      title: 'Company Settings',
      description: 'Manage company configurations',
      icon: <Building2 className="h-5 w-5" />,
      onClick: () => navigate('/admin/company'),
      color: 'bg-emerald-500 hover:bg-emerald-600'
    },
    {
      title: 'Work Order Templates',
      description: 'Configure work order templates',
      icon: <FileText className="h-5 w-5" />,
      onClick: () => navigate('/admin/templates'),
      color: 'bg-violet-500 hover:bg-violet-600'
    },
    {
      title: 'View Statistics',
      description: 'Company performance metrics',
      icon: <BarChart3 className="h-5 w-5" />,
      onClick: () => navigate('/admin/dashboard'),
      color: 'bg-rose-500 hover:bg-rose-600'
    }
  ];

  const ActionGroup: React.FC<{ title: string; actions: any[]; icon: React.ReactNode }> = ({ 
    title, 
    actions, 
    icon 
  }) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          {icon}
          <span>{title}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {actions.map((action, index) => (
          <Button
            key={index}
            variant="outline"
            className={`w-full justify-start ${action.color} text-white border-0`}
            onClick={action.onClick}
          >
            <div className="flex items-center space-x-3">
              {action.icon}
              <div className="text-left">
                <div className="font-medium">{action.title}</div>
                <div className="text-xs opacity-90">{action.description}</div>
              </div>
            </div>
          </Button>
        ))}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <ActionGroup
        title="User Management"
        actions={userManagementActions}
        icon={<Users className="h-5 w-5" />}
      />
      
      <ActionGroup
        title="System Management"
        actions={systemManagementActions}
        icon={<Shield className="h-5 w-5" />}
      />
      
      <ActionGroup
        title="Data Management"
        actions={dataManagementActions}
        icon={<Database className="h-5 w-5" />}
      />
      
      <ActionGroup
        title="Company Management"
        actions={companyManagementActions}
        icon={<Building2 className="h-5 w-5" />}
      />
    </div>
  );
};

export default AdminQuickActions;
